import { Button } from 'lego-on-react';
import React, { useEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import { getI18nLang } from '@yandex-int/i18n';
import { getIsYangoRequest, getQuasarUiUrl } from 'client/utils';
import * as ksCommon from 'i18n/Common';
import * as keyset from 'i18n/Testing';
import block from 'propmods';

import { useMessageSubscription } from '../../hooks/useMessageSubscription';
import { Skill } from '../../model/skill';
import { openAccountLinkingWindow } from '../../utils/accountLinking';
import { quasar } from '../../utils/messages';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import SmartHomeTestPageHistoryHighlighter from '../SmartHomeTestPageHistoryHighlighter/SmartHomeTestPageHistoryHighlighter';
import Switch from '../Switch/Switch';
import { H2, PageHeadline } from '../Typo/Typo';

import { SmartHomeTestPageChecklist } from './SmartHomeTestPageChecklist';

import './SmartHomeTestPage.scss';

const b = block('SmartHomeTestPage');
const t = i18n(keyset);
const tCommon = i18n(ksCommon);

interface Props {
    skill: Skill;
    onTypeChange: (isDraft: boolean) => void;
    isDraft: boolean;
}

export const appendLogs = (logs: string[], newLogs: string[]) => {
    const maxLogs = 500;

    const clampedLogs = [...logs, ...newLogs];

    return clampedLogs.length <= maxLogs ? clampedLogs : clampedLogs.slice(clampedLogs.length - maxLogs);
};

const SmartHomeTestPage: React.FC<Props> = ({ skill, onTypeChange, isDraft }) => {
    const [debugInfo, setDebugInfo] = useState<string[]>([]);

    useMessageSubscription((data) => {
        switch (data.type) {
            case 'dialogs_account-linking_start': {
                const applicationName = encodeURIComponent(data.payload.applicationName);
                const url = `${__appConfig.app.webLinkingUrl}?consumer=passport&application_name=${applicationName}`;

                openAccountLinkingWindow({
                    url,
                    onSuccess: () => quasar.sendAccountLinkingResult('ok'),
                    onFailure: () => quasar.sendAccountLinkingResult('error'),
                });

                return;
            }
            case 'dialogs_debug-info': {
                if (data.payload.debugInfo && data.payload.debugInfo.logs) {
                    setDebugInfo(appendLogs(debugInfo, data.payload.debugInfo.logs));
                } else {
                    console.error('Malformed debug info');
                }

                return;
            }
        }
    });

    useEffect(() => {
        setDebugInfo([]);
    }, [isDraft]);

    const testedSkillId = isDraft ? `${skill.id}-draft` : skill.id;
    const isYangoRequest = getIsYangoRequest();
    const lang = getI18nLang() === 'ru' ? 'ru-RU' : 'en-US';
    const frameSrc = isYangoRequest
        ? `${getQuasarUiUrl()}?lang=${lang}&app_id=com.yasmina.iot&from=dialogs&tested_skill_id=${testedSkillId}`
        : `${getQuasarUiUrl()}?from=dialogs&tested_skill_id=${testedSkillId}`;

    const debugString = debugInfo.join('\n\n');

    return (
        <ErrorBoundary withHomepageLink>
            <div {...b()}>
                <PageHeadline
                    title={t('headline')}
                    subtitle={
                        <Switch
                            items={[
                                { value: 'draft', text: tCommon('draft') },
                                {
                                    value: 'production',
                                    text: tCommon('published'),
                                    title: skill.onAir ? void 0 : 'Вы еще не опубликовали ни одной версии диалога',
                                    disabled: !skill.onAir,
                                },
                            ]}
                            value={isDraft ? 'draft' : 'production'}
                            onSwitch={(value) => onTypeChange(value === 'draft')}
                        />
                    }
                />

                {!isDraft || skill.draft.isAllowedForDeploy ? (
                    <>
                        <div {...b('checklist')}>
                            <SmartHomeTestPageChecklist displayIcon />
                        </div>
                        <main {...b('main')}>
                            <section {...b('frame-column')}>
                                <div {...b('head-toolbar')}>
                                    <H2>{t('smart-home')}</H2>
                                    <div {...b('head-toolbar-button')}>
                                        <Button size="xs" theme="normal" onClick={quasar.moveToDeviceList}>
                                            {t('devices')}
                                        </Button>
                                    </div>
                                </div>
                                <iframe src={frameSrc} key={testedSkillId} />
                            </section>
                            <section {...b('history-column')}>
                                <H2>{t('log')}</H2>
                                <section {...b('history')}>
                                    <SmartHomeTestPageHistoryHighlighter data={debugString} />
                                </section>
                            </section>
                        </main>
                    </>
                ) : (
                    <div>{t('fill-fields-to-test')}</div>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default SmartHomeTestPage;
